import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BookOpen, Monitor, Smartphone, Users, ChevronDown, ChevronRight,
  BarChart3, Calendar, Clock, Timer, ArrowLeftRight, FileSpreadsheet,
  Building2, Shield, Settings, FileText, Bell, Fingerprint, Wifi,
  UserCog, UserCheck, User, HelpCircle, CheckCircle2, QrCode, KeyRound,
  Library, Brain, ClipboardCheck, Search,
} from 'lucide-react';
import ClockingMachineModeArticle from '../components/ClockingMachineModeArticle';

const tabs = ['webGuide', 'mobileGuide', 'accountTypes'];
const tabIcons = { webGuide: Monitor, mobileGuide: Smartphone, accountTypes: Users };

export default function HowToGuides() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('webGuide');
  const [expandedSection, setExpandedSection] = useState('gettingStarted');

  const toggle = (key) => setExpandedSection(prev => prev === key ? null : key);

  const webFeatures = [
    { key: 'dashboard', icon: BarChart3, color: 'text-blue-400' },
    { key: 'rota', icon: Calendar, color: 'text-indigo-400' },
    { key: 'staff', icon: Users, color: 'text-purple-400' },
    { key: 'groups', icon: Building2, color: 'text-teal-400' },
    { key: 'timeClock', icon: Timer, color: 'text-green-400' },
    { key: 'terminal', icon: QrCode, color: 'text-cyan-400' },
    { key: 'availability', icon: Clock, color: 'text-yellow-400' },
    { key: 'swaps', icon: ArrowLeftRight, color: 'text-orange-400' },
    { key: 'swapBoard', icon: ArrowLeftRight, color: 'text-pink-400' },
    { key: 'payroll', icon: FileSpreadsheet, color: 'text-emerald-400' },
    { key: 'analytics', icon: BarChart3, color: 'text-cyan-400' },
    { key: 'reports', icon: FileText, color: 'text-rose-400' },
    {
      key: 'policies',
      icon: Library,
      color: 'text-teal-400',
      titleFallback: 'Policies & Procedures',
      descFallback: 'Upload controlled documents, manage versions, assign sign-off, add assessments and track staff training evidence.',
    },
    {
      key: 'knowledgeHub',
      icon: Brain,
      color: 'text-sky-400',
      titleFallback: 'Knowledge Hub',
      descFallback: 'Search policies, monitor compliance status, review training records and ask AI questions from approved policy summaries.',
    },
    { key: 'auditLog', icon: Shield, color: 'text-amber-400' },
    { key: 'settings', icon: Settings, color: 'text-gray-400' },
    { key: 'subscription', icon: Settings, color: 'text-violet-400' },
  ];

  const mobileFeatures = [
    { key: 'mobileMyShifts', icon: Calendar, color: 'text-blue-400' },
    { key: 'mobileRota', icon: Calendar, color: 'text-indigo-400' },
    { key: 'mobileTimeClock', icon: Timer, color: 'text-green-400' },
    { key: 'mobileTerminal', icon: KeyRound, color: 'text-cyan-400' },
    { key: 'mobileSwaps', icon: ArrowLeftRight, color: 'text-orange-400' },
    { key: 'mobileAvailability', icon: Clock, color: 'text-yellow-400' },
    { key: 'mobileNotifications', icon: Bell, color: 'text-pink-400' },
    {
      key: 'mobilePolicies',
      icon: Library,
      color: 'text-teal-400',
      textFallback: 'Read assigned policies, complete assessments and sign documents from the mobile Policies tab.',
    },
    {
      key: 'mobilePolicySearch',
      icon: Search,
      color: 'text-sky-400',
      textFallback: 'Ask AI policy questions on the go and get answers from approved policy summaries.',
    },
    { key: 'mobileBiometric', icon: Fingerprint, color: 'text-purple-400' },
    { key: 'mobileOffline', icon: Wifi, color: 'text-cyan-400' },
  ];

  const roles = [
    {
      key: 'admin',
      icon: UserCog,
      color: 'from-red-500/20 to-red-500/5',
      border: 'border-red-500/30',
      badge: 'bg-red-500/20 text-red-400',
      perms: 7,
    },
    {
      key: 'manager',
      icon: UserCheck,
      color: 'from-amber-500/20 to-amber-500/5',
      border: 'border-amber-500/30',
      badge: 'bg-amber-500/20 text-amber-400',
      perms: 5,
    },
    {
      key: 'staff',
      icon: User,
      color: 'from-blue-500/20 to-blue-500/5',
      border: 'border-blue-500/30',
      badge: 'bg-blue-500/20 text-blue-400',
      perms: 5,
    },
  ];

  const quickStart = [
    { key: 'quick1', icon: Users, color: 'text-blue-400' },
    { key: 'quick2', icon: Building2, color: 'text-teal-400' },
    { key: 'quick3', icon: Calendar, color: 'text-indigo-400' },
    { key: 'quick4', icon: QrCode, color: 'text-cyan-400' },
    { key: 'quick5', icon: FileSpreadsheet, color: 'text-emerald-400' },
  ];

  const webPolicySteps = [
    {
      title: t('guide.policyWebStep1Title', 'Open Policies & Procedures'),
      desc: t('guide.policyWebStep1Desc', 'Use the Policies tab to upload controlled documents, assign users, roles or groups, set review intervals, expiry dates and version details.'),
    },
    {
      title: t('guide.policyWebStep2Title', 'Generate a policy summary'),
      desc: t('guide.policyWebStep2Desc', 'For long documents, generate or paste a reusable AI summary first. Assessment questions and Knowledge Hub answers can use this summary without rescanning the full file every time.'),
    },
    {
      title: t('guide.policyWebStep3Title', 'Add assessments and sign-off rules'),
      desc: t('guide.policyWebStep3Desc', 'Enable closeout assessments, set pass thresholds, require successful completion before sign-off, and capture digital signatures for audit evidence.'),
    },
    {
      title: t('guide.policyWebStep4Title', 'Review the Knowledge Hub'),
      desc: t('guide.policyWebStep4Desc', 'Use Knowledge Hub to search policies, review document status, monitor pending or overdue training, inspect assessment submissions and export audit-ready records.'),
    },
  ];

  const mobilePolicySteps = [
    {
      title: t('guide.policyMobileStep1Title', 'Open the Policies tab'),
      desc: t('guide.policyMobileStep1Desc', 'Staff can view assigned policies and see which documents still need reading, assessment or sign-off.'),
    },
    {
      title: t('guide.policyMobileStep2Title', 'Read, assess and sign'),
      desc: t('guide.policyMobileStep2Desc', 'Open a policy, review the document, complete any required assessment and add a signature to confirm read-and-understood compliance.'),
    },
    {
      title: t('guide.policyMobileStep3Title', 'Ask policy questions on the go'),
      desc: t('guide.policyMobileStep3Desc', 'Use the AI policy search box to ask natural language questions and receive answers from approved policy summaries with source policy versions.'),
    },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary/20 to-blue-600/10 border border-primary/20 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen size={28} className="text-primary" />
          <h1 className="text-2xl font-bold text-white">{t('guide.title')}</h1>
        </div>
        <p className="text-gray-400">{t('guide.subtitle')}</p>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-2">
        {tabs.map(tab => {
          const Icon = tabIcons[tab];
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab
                  ? 'bg-primary text-white shadow-lg shadow-primary/20'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
              }`}
            >
              <Icon size={16} />
              {t(`guide.${tab}`)}
            </button>
          );
        })}
      </div>

      {/* WEB GUIDE */}
      {activeTab === 'webGuide' && (
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-primary/15 to-cyan-500/10 border border-primary/20 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle2 size={22} className="text-primary" />
              <h2 className="text-lg font-semibold text-white">{t('guide.quickStartTitle')}</h2>
            </div>
            <p className="text-sm text-gray-300 mb-4">{t('guide.quickStartDesc')}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {quickStart.map(({ key, icon: Icon, color }) => (
                <div key={key} className="bg-white/5 rounded-lg p-3 border border-white/10 flex items-start gap-3">
                  <Icon size={18} className={`${color} mt-0.5 flex-shrink-0`} />
                  <div>
                    <h3 className="text-sm font-semibold text-white">{t(`guide.${key}Title`)}</h3>
                    <p className="text-xs text-gray-400 mt-1 leading-relaxed">{t(`guide.${key}Desc`)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Getting Started */}
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <button
              onClick={() => toggle('gettingStarted')}
              className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
            >
              <div>
                <h2 className="text-lg font-semibold text-white">{t('guide.gettingStarted')}</h2>
                <p className="text-sm text-gray-400 mt-0.5">{t('guide.gettingStartedDesc')}</p>
              </div>
              {expandedSection === 'gettingStarted' ? <ChevronDown size={20} className="text-gray-400" /> : <ChevronRight size={20} className="text-gray-400" />}
            </button>
            {expandedSection === 'gettingStarted' && (
              <div className="px-5 pb-5 space-y-4">
                {[1, 2, 3].map(step => (
                  <div key={step} className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-lg">
                      {step}
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{t(`guide.step${step}Title`)}</h3>
                      <p className="text-sm text-gray-400 mt-1">{t(`guide.step${step}Desc`)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Web Features */}
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <button
              onClick={() => toggle('webFeatures')}
              className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
            >
              <div>
                <h2 className="text-lg font-semibold text-white">{t('guide.webFeatures')}</h2>
              </div>
              {expandedSection === 'webFeatures' ? <ChevronDown size={20} className="text-gray-400" /> : <ChevronRight size={20} className="text-gray-400" />}
            </button>
            {expandedSection === 'webFeatures' && (
              <div className="px-5 pb-5 grid gap-3 sm:grid-cols-2">
                {webFeatures.map(({ key, icon: Icon, color, titleFallback, descFallback }) => (
                  <div key={key} className="bg-white/5 rounded-lg p-4 border border-white/5">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon size={18} className={color} />
                      <h3 className="font-semibold text-white text-sm">{t(`guide.${key}Title`, titleFallback)}</h3>
                    </div>
                    <p className="text-xs text-gray-400 leading-relaxed">{t(`guide.${key}Desc`, descFallback)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Policies & Knowledge Hub */}
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <button
              onClick={() => toggle('policyGuide')}
              className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
            >
              <div>
                <h2 className="text-lg font-semibold text-white">
                  {t('guide.policyGuideTitle', 'Policies, Procedures & Knowledge Hub')}
                </h2>
                <p className="text-sm text-gray-400 mt-0.5">
                  {t('guide.policyGuideDesc', 'Centralise controlled documents, track training records, and use AI policy search across shared BuildMyChecks and BuildMyShifts policy content.')}
                </p>
              </div>
              {expandedSection === 'policyGuide' ? <ChevronDown size={20} className="text-gray-400" /> : <ChevronRight size={20} className="text-gray-400" />}
            </button>
            {expandedSection === 'policyGuide' && (
              <div className="px-5 pb-5 space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="bg-teal-500/10 rounded-lg p-4 border border-teal-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Library size={18} className="text-teal-300" />
                      <h3 className="font-semibold text-white text-sm">{t('guide.policySingleSourceTitle', 'Shared policy source')}</h3>
                    </div>
                    <p className="text-xs text-gray-300 leading-relaxed">
                      {t('guide.policySingleSourceDesc', 'If the same organisation uses BuildMyChecks and BuildMyShifts, policies can be shared so version control, acknowledgements and assessments stay consistent across both products.')}
                    </p>
                  </div>
                  <div className="bg-sky-500/10 rounded-lg p-4 border border-sky-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Brain size={18} className="text-sky-300" />
                      <h3 className="font-semibold text-white text-sm">{t('guide.policyAiTitle', 'AI-assisted answers')}</h3>
                    </div>
                    <p className="text-xs text-gray-300 leading-relaxed">
                      {t('guide.policyAiDesc', 'Generate approved summaries from policy documents, then use Knowledge Hub to ask questions without sending the full document to AI every time.')}
                    </p>
                  </div>
                </div>
                {webPolicySteps.map((step, index) => (
                  <div key={step.title} className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-teal-500/20 text-teal-300 flex items-center justify-center font-bold text-lg">
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{step.title}</h3>
                      <p className="text-sm text-gray-400 mt-1">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* MOBILE GUIDE */}
      {activeTab === 'mobileGuide' && (
        <div className="space-y-4">
          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <Smartphone size={22} className="text-primary" />
              <h2 className="text-lg font-semibold text-white">{t('guide.mobileFeatures')}</h2>
            </div>
            <p className="text-gray-400 text-sm mb-5">{t('guide.mobileIntro')}</p>
            <div className="space-y-3">
              {mobileFeatures.map(({ key, icon: Icon, color, textFallback }) => (
                <div key={key} className="flex items-start gap-3 bg-white/5 rounded-lg p-3 border border-white/5">
                  <Icon size={18} className={`${color} mt-0.5 flex-shrink-0`} />
                  <p className="text-sm text-gray-300">{t(`guide.${key}`, textFallback)}</p>
                </div>
              ))}
            </div>
          </div>

          <ClockingMachineModeArticle />

          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <ClipboardCheck size={22} className="text-teal-400" />
              <h2 className="text-lg font-semibold text-white">
                {t('guide.mobilePolicyGuideTitle', 'Policies & AI Search on Mobile')}
              </h2>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              {t('guide.mobilePolicyGuideDesc', 'Use the mobile Policies tab for quick on-site reading, sign-off, assessment completion and AI policy questions.')}
            </p>
            <div className="space-y-3">
              {mobilePolicySteps.map((step, index) => (
                <div key={step.title} className="flex gap-3 items-start bg-white/5 rounded-lg p-3 border border-white/5">
                  <div className="w-7 h-7 rounded-full bg-teal-500/15 text-teal-300 flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-white">{step.title}</h3>
                    <p className="text-xs text-gray-400 mt-1 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ACCOUNT TYPES */}
      {activeTab === 'accountTypes' && (
        <div className="space-y-4">
          {roles.map(({ key, icon: Icon, color, border, badge, perms }) => (
            <div key={key} className={`bg-gradient-to-br ${color} border ${border} rounded-xl p-5`}>
              <div className="flex items-center gap-3 mb-3">
                <Icon size={22} className="text-white" />
                <h2 className="text-lg font-semibold text-white">{t(`guide.${key}Account`)}</h2>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge}`}>
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </span>
              </div>
              <p className="text-gray-300 text-sm mb-3">{t(`guide.${key}Desc`)}</p>
              <ul className="space-y-2">
                {Array.from({ length: perms }, (_, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle2 size={14} className="text-green-400 flex-shrink-0" />
                    {t(`guide.${key}Perm${i + 1}`)}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Help footer */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-5 flex items-start gap-3">
        <HelpCircle size={20} className="text-primary mt-0.5 flex-shrink-0" />
        <div>
          <h3 className="font-semibold text-white">{t('guide.needHelp')}</h3>
          <p className="text-sm text-gray-400 mt-1">{t('guide.needHelpDesc')}</p>
        </div>
      </div>
    </div>
  );
}
